import { msg } from "@lingui/core/macro";
import { createMemo, createSignal, For, Show } from "solid-js";
import {
  AlertTriangle,
  CheckCircle2,
  ChevronRight,
  Loader2,
  Palette,
  Scan,
  ShieldAlert,
  Wrench,
} from "@fleet/icons";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@fleet/ui/card";
import { Badge } from "@fleet/ui/badge";
import { Skeleton } from "@fleet/ui/skeleton";
import { api, createApiQuery } from "@/lib/api";
import { cn, type components, getAdminThemeAuditHandlers } from "@agape/shared";
import { showToast } from "@/components/ui/toast";
import { useI18n } from "@/providers/i18n-provider";

type SingleAuditResult = components["schemas"]["ThemeAuditResult"];
type AllAuditResult = components["schemas"]["ThemeAuditAllResult"];
type ThemeRow = components["schemas"]["ThemeDTO"];

const SKELETON_COUNT = 5;

export function ThemeAudit() {
  const { t } = useI18n();
  const [singleResult, setSingleResult] = createSignal<SingleAuditResult | null>(null);
  const [allResult, setAllResult] = createSignal<AllAuditResult | null>(null);
  const [isAuditing, setIsAuditing] = createSignal(false);
  const [isRepairing, setIsRepairing] = createSignal(false);
  const [repairError, setRepairError] = createSignal<unknown>(null);
  const [auditError, setAuditError] = createSignal<Error | null>(null);

  const themesQuery = createApiQuery<ThemeRow[], components["schemas"]["ErrorModel"]>(
    () => ["admin", "themes"],
    async () => api.GET("/api/admin/theme/listAll"),
    () => ({ staleTime: 30_000 }),
  );

  const handlers = createMemo(() =>
    getAdminThemeAuditHandlers({
      actions: {
        auditSingle: async (id) => {
          const { data, error } = await api.POST("/api/admin/theme/audit", {
            body: { themeId: id },
          });
          return { data: data ?? null, error };
        },
        auditAll: async () => {
          const { data, error } = await api.GET("/api/admin/theme/auditAll");
          return { data: data ?? null, error };
        },
        repairPairs: async () => {
          const { data, error } = await api.POST("/api/admin/theme/repairPairs");
          return { data: data ?? null, error };
        },
      },
      setSingleResult,
      setAllResult,
      setIsAuditing,
      setIsRepairing,
      onRepairSuccess: (result: { clearedCount: number; repairedCount: number }) => {
        setRepairError(null);
        const cleared = result.clearedCount;
        const repaired = result.repairedCount;
        showToast(
          t(msg`Repair completed: ${cleared} pair(s) deleted, ${repaired} pair(s) repaired`),
          "success",
        );
        void themesQuery.refetch();
      },
      onRepairError: (err) => {
        setRepairError(err);
        const errMsg = err instanceof Error ? err.message : String(err);
        showToast(t(msg`Repair error: ${errMsg}`), "error");
      },
      onAuditError: (err) => {
        setAuditError(err);
        const { message } = err;
        showToast(t(msg`Audit error: ${message}`), "error");
      },
    }),
  );

  return (
    <div class="space-y-8">
      {/* Header */}
      <div class="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div class="flex items-center gap-3">
          <ShieldAlert class="h-8 w-8 text-primary" />
          <div>
            <h2 class="text-3xl font-bold tracking-tight">{t(msg`Theme Audit`)}</h2>
            <p class="text-sm text-muted-foreground">
              {t(
                msg`Checks the theme catalog for integrity problems — empty required colors (background, surface, accent) and dangling light/dark pairings (a paired theme that no longer exists). "Repair pairs" clears those broken links; otherwise this is read-only.`,
              )}
            </p>
          </div>
        </div>
        <div class="flex flex-wrap gap-2">
          <Button
            variant="outline"
            onClick={() => void handlers().runRepair()}
            disabled={isRepairing() || isAuditing()}
            class="gap-2"
          >
            <Show when={isRepairing()} fallback={<Wrench class="h-4 w-4" />}>
              <Loader2 class="h-4 w-4 animate-spin" />
            </Show>
            {t(msg`Repair Pairs`)}
          </Button>
          <Button
            variant="default"
            onClick={() => void handlers().runAll()}
            disabled={isAuditing() || isRepairing()}
            class="gap-2"
          >
            <Show when={isAuditing() && !singleResult()} fallback={<Scan class="h-4 w-4" />}>
              <Loader2 class="h-4 w-4 animate-spin" />
            </Show>
            {t(msg`Audit All`)}
          </Button>
        </div>
      </div>

      {/* Errors display */}
      <Show when={auditError()}>
        <Card class="border-destructive bg-destructive/5">
          <CardContent class="p-4 flex items-center gap-3 text-destructive">
            <AlertTriangle class="h-5 w-5" />
            <p class="text-sm font-medium">{auditError()?.message}</p>
          </CardContent>
        </Card>
      </Show>

      <Show when={repairError()}>
        <Card class="border-destructive bg-destructive/5">
          <CardContent class="p-4 flex items-center gap-3 text-destructive">
            <AlertTriangle class="h-5 w-5" />
            <p class="text-sm font-medium">
              {(() => {
                const err = repairError();
                return err instanceof Error ? err.message : String(err);
              })()}
            </p>
          </CardContent>
        </Card>
      </Show>

      {/* Two column layout */}
      <div class="grid gap-6 lg:grid-cols-4">
        {/* Sidebar Theme List */}
        <aside class="lg:col-span-1 space-y-4">
          <h3 class="text-sm font-semibold uppercase tracking-wider text-muted-foreground">
            {t(msg`Available Themes`)}
          </h3>
          <div class="flex flex-col gap-1 max-h-[600px] overflow-y-auto pr-2">
            <For
              each={themesQuery.data}
              fallback={
                <div class="space-y-2">
                  <For each={Array.from({ length: SKELETON_COUNT })}>
                    {() => <Skeleton class="h-9 w-full" />}
                  </For>
                </div>
              }
            >
              {(theme) => (
                <Button
                  variant={singleResult()?.themeId === theme.id ? "secondary" : "ghost"}
                  class="justify-between group px-3 h-10"
                  onClick={() => void handlers().runSingle(theme.id)}
                  disabled={isAuditing() || isRepairing()}
                >
                  <div class="flex items-center gap-2 truncate">
                    <div
                      class="h-3.5 w-3.5 rounded-full border border-border shrink-0"
                      style={{ "background-color": theme.background }}
                    />
                    <span class="truncate text-sm">{theme.name}</span>
                  </div>
                  <ChevronRight class="h-4 w-4 opacity-0 group-hover:opacity-100 transition-opacity shrink-0" />
                </Button>
              )}
            </For>
          </div>
        </aside>

        {/* Audit Results Main Area */}
        <main class="lg:col-span-3 space-y-6">
          <Show
            when={singleResult()}
            fallback={
              <Show
                when={allResult()}
                fallback={
                  <Card class="border-dashed flex flex-col items-center justify-center p-12 text-center">
                    <div class="p-4 rounded-full bg-primary/5 text-primary mb-4">
                      <Palette class="h-8 w-8" />
                    </div>
                    <h3 class="font-semibold text-lg">{t(msg`No Audit Performed`)}</h3>
                    <p class="text-sm text-muted-foreground max-w-sm mt-1">
                      {t(
                        msg`Select a theme from the sidebar to view individual issues, or click "Audit All" to scan the entire system.`,
                      )}
                    </p>
                  </Card>
                }
              >
                {(audit) => (
                  <Card>
                    <CardHeader>
                      <CardTitle>{t(msg`System Audit Summary`)}</CardTitle>
                      <div class="grid grid-cols-2 md:grid-cols-4 gap-4 pt-3">
                        <div class="bg-muted/40 p-4 rounded-xl border flex flex-col items-center">
                          <span class="text-2xl font-bold">{audit().total}</span>
                          <span class="text-xs text-muted-foreground">{t(msg`Total Themes`)}</span>
                        </div>
                        <div class="bg-muted/40 p-4 rounded-xl border flex flex-col items-center">
                          <span class="text-2xl font-bold text-primary">{audit().active}</span>
                          <span class="text-xs text-muted-foreground">{t(msg`Active Themes`)}</span>
                        </div>
                        <div class="bg-muted/40 p-4 rounded-xl border flex flex-col items-center">
                          <span class="text-2xl font-bold">{audit().paired}</span>
                          <span class="text-xs text-muted-foreground">{t(msg`Paired Themes`)}</span>
                        </div>
                        <div
                          class={cn(
                            "p-4 rounded-xl border flex flex-col items-center",
                            audit().withIssues > 0
                              ? "bg-destructive/5 border-destructive/20 text-destructive"
                              : "bg-success/5 border-success/20 text-success",
                          )}
                        >
                          <span class="text-2xl font-bold">{audit().withIssues}</span>
                          <span class="text-xs text-muted-foreground opacity-80">
                            {t(msg`With Issues`)}
                          </span>
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent class="space-y-3">
                      <h4 class="font-semibold text-sm">{t(msg`Theme List Diagnostics`)}</h4>
                      <div class="divide-y rounded-xl border overflow-hidden">
                        <For each={audit().themes}>
                          {(theme) => {
                            const mode = theme.isDark ? t(msg`Dark theme`) : t(msg`Light theme`);
                            const status = theme.isActive ? t(msg`Active`) : t(msg`Inactive`);
                            const order = theme.sortOrder;
                            const issueCount = theme.issues?.length ?? 0;
                            return (
                              <div class="flex items-center justify-between p-4 bg-card hover:bg-muted/10 transition-colors">
                                <div>
                                  <p class="font-bold text-sm">{theme.name}</p>
                                  <p class="text-xs text-muted-foreground">
                                    {t(msg`${mode} • ${status} • Sort Order: ${order}`)}
                                  </p>
                                </div>
                                <div class="flex items-center gap-3">
                                  <Show
                                    when={theme.hasIssues}
                                    fallback={
                                      <div class="flex items-center gap-1.5 text-xs text-success bg-success/10 px-2 py-1 rounded-full font-medium">
                                        <CheckCircle2 class="h-3.5 w-3.5" />
                                        {t(msg`Healthy`)}
                                      </div>
                                    }
                                  >
                                    <div class="flex items-center gap-1.5 text-xs text-destructive bg-destructive/10 px-2 py-1 rounded-full font-medium">
                                      <AlertTriangle class="h-3.5 w-3.5" />
                                      {t(msg`${issueCount} Issues`)}
                                    </div>
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      onClick={() => void handlers().runSingle(theme.themeId)}
                                    >
                                      {t(msg`Details`)}
                                    </Button>
                                  </Show>
                                </div>
                              </div>
                            );
                          }}
                        </For>
                      </div>
                    </CardContent>
                  </Card>
                )}
              </Show>
            }
          >
            {(audit) => {
              const { themeId } = audit();
              const auditedTime = new Date(audit().auditedAt).toLocaleTimeString();
              return (
                <Card>
                  <CardHeader class="flex-row items-center justify-between space-y-0 pb-4 border-b">
                    <div>
                      <CardTitle class="text-2xl">{audit().name}</CardTitle>
                      <p class="text-xs text-muted-foreground mt-1">
                        {t(msg`Theme ID: ${themeId} • Audited: ${auditedTime}`)}
                      </p>
                    </div>
                    <Badge variant={audit().hasIssues ? "destructive" : "outline"}>
                      {audit().hasIssues ? t(msg`Issues Detected`) : t(msg`Valid & Healthy`)}
                    </Badge>
                  </CardHeader>
                  <CardContent class="pt-6 space-y-6">
                    {/* Quick info */}
                    <div class="grid grid-cols-3 gap-4">
                      <div class="border rounded-xl p-3 bg-muted/20">
                        <span class="text-xs text-muted-foreground block">
                          {t(msg`Brightness`)}
                        </span>
                        <span class="text-sm font-semibold mt-1 block capitalize">
                          {audit().isDark ? t(msg`Dark`) : t(msg`Light`)} {t(msg`Mode`)}
                        </span>
                      </div>
                      <div class="border rounded-xl p-3 bg-muted/20">
                        <span class="text-xs text-muted-foreground block">
                          {t(msg`Usage State`)}
                        </span>
                        <span class="text-sm font-semibold mt-1 block capitalize">
                          {audit().isActive ? t(msg`Active`) : t(msg`Inactive`)}
                        </span>
                      </div>
                      <div class="border rounded-xl p-3 bg-muted/20">
                        <span class="text-xs text-muted-foreground block">
                          {t(msg`Sort Order`)}
                        </span>
                        <span class="text-sm font-semibold mt-1 block text-xs truncate">
                          {audit().sortOrder}
                        </span>
                      </div>
                    </div>

                    <Show
                      when={audit().hasIssues}
                      fallback={
                        <div class="flex items-center gap-3 p-4 border border-success/20 bg-success/5 rounded-xl text-success">
                          <CheckCircle2 class="h-6 w-6 shrink-0" />
                          <div>
                            <p class="font-semibold text-sm">{t(msg`Theme is Perfect`)}</p>
                            <p class="text-xs opacity-90">
                              {t(
                                msg`All contrast settings, backgrounds, and accents conform to the standard design system.`,
                              )}
                            </p>
                          </div>
                        </div>
                      }
                    >
                      <div class="space-y-3">
                        <h4 class="font-semibold text-sm text-destructive">
                          {t(msg`Discovered Inconsistencies`)}
                        </h4>
                        <div class="space-y-2">
                          <For each={audit().issues ?? []}>
                            {(issue) => (
                              <div class="flex items-start gap-3 p-4 border border-destructive/20 bg-destructive/5 rounded-xl text-destructive">
                                <AlertTriangle class="h-5 w-5 shrink-0 mt-0.5" />
                                <div class="space-y-1">
                                  <p class="font-bold text-sm capitalize">{issue.field}</p>
                                  <p class="text-xs text-muted-foreground">{issue.issue}</p>
                                  <Show when={issue.value !== null && issue.value !== undefined}>
                                    <div class="pt-1">
                                      <span class="text-[10px] bg-muted px-2 py-0.5 rounded font-mono border border-border text-foreground">
                                        {t(msg`Value`)}:{" "}
                                        {typeof issue.value === "object"
                                          ? JSON.stringify(issue.value)
                                          : String(issue.value)}
                                      </span>
                                    </div>
                                  </Show>
                                </div>
                              </div>
                            )}
                          </For>
                        </div>
                      </div>
                    </Show>
                  </CardContent>
                </Card>
              );
            }}
          </Show>
        </main>
      </div>
    </div>
  );
}
